import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Check } from 'lucide-react'
import { useState, type FormEvent } from 'react'
import { ConfirmButton } from '@/components/ConfirmButton'
import { FormError } from '@/components/FormError'
import { Initial } from '@/components/paper'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { api } from '@/lib/api'
import type { Family, Space } from '@/lib/families'
import { cn } from '@/lib/utils'

// A circle's custom spaces: rooms for some of its people, like "The brothers". What's kept in or shared with one is
// only for the people in it. You see the ones you're in; anyone in one can rename it or change who's in it.
export function CircleSpaces({ family, me }: { family: Family; me: string }) {
  const [editing, setEditing] = useState<Space | 'new' | null>(null)
  const custom = family.spaces.filter((x) => x.kind === 'custom')
  const nameOf = (id: string) => (id === me ? 'You' : (family.members.find((x) => x.id === id)?.name ?? 'Someone'))
  return (
    <section className="flex flex-col gap-4 pt-4" aria-labelledby="spaces">
      <h2 id="spaces" className="text-title">
        Spaces
      </h2>
      <p className="text-muted-foreground">
        Rooms for some of the circle, like the brothers or the cousins. What’s shared with a space is only for the
        people in it. The tree is the same in all of them.
      </p>
      {custom.length > 0 && (
        <ul className="flex flex-col gap-2">
          {custom.map((space) => (
            <li key={space.id} className="flex items-center justify-between gap-3">
              <span className="flex min-w-0 flex-col">
                <span className="truncate font-medium">{space.name}</span>
                <span className="truncate text-sub text-muted-foreground">
                  {[...(space.members ?? [])]
                    .sort((a, b) => Number(b === me) - Number(a === me))
                    .map(nameOf)
                    .join(', ')}
                </span>
              </span>
              <Button variant="outline" size="sm" onClick={() => setEditing(space)}>
                Change
              </Button>
            </li>
          ))}
        </ul>
      )}
      <Button variant="outline" className="self-start" onClick={() => setEditing('new')}>
        Make a space
      </Button>
      <SpaceDialog family={family} me={me} editing={editing} onClose={() => setEditing(null)} />
    </section>
  )
}

// Making a space ('new'), or changing one, in a dialog. Closed when there's nothing to edit.
export function SpaceDialog({
  family,
  me,
  editing,
  onClose,
}: {
  family: Family
  me: string
  editing: Space | 'new' | null
  onClose: () => void
}) {
  return (
    <Dialog open={editing !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90svh] overflow-y-auto sm:max-w-lg">
        {editing && (
          <SpaceForm
            key={editing === 'new' ? 'new' : editing.id}
            family={family}
            me={me}
            space={editing === 'new' ? null : editing}
            onDone={onClose}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}

function SpaceForm({
  family,
  me,
  space,
  onDone,
}: {
  family: Family
  me: string
  space: Space | null
  onDone: () => void
}) {
  const queryClient = useQueryClient()
  const [name, setName] = useState(space?.name ?? '')
  // Making one, you're always in it; changing one, leaving yourself off the list is leaving.
  const [members, setMembers] = useState<string[]>(space?.members ?? [me])
  const refresh = () =>
    Promise.all(
      [['family', family.id], ['families'], ['circles'], ['share-places']].map((queryKey) =>
        queryClient.invalidateQueries({ queryKey }),
      ),
    )
  const save = useMutation({
    mutationFn: () =>
      space
        ? api(`/spaces/${space.id}`, { method: 'PATCH', body: { name: name.trim(), members } })
        : api(`/families/${family.id}/spaces`, { method: 'POST', body: { name: name.trim(), members } }),
    onSuccess: async () => {
      await refresh()
      onDone()
    },
  })
  const leave = useMutation({
    mutationFn: () => api(`/spaces/${space!.id}/leave`, { method: 'POST' }),
    onSuccess: async () => {
      await refresh()
      onDone()
    },
  })
  const picked = new Set(members)
  const toggle = (id: string) => setMembers(picked.has(id) ? members.filter((x) => x !== id) : [...members, id])
  const choices = space ? family.members : family.members.filter((x) => x.id !== me)

  function submit(e: FormEvent) {
    e.preventDefault()
    save.mutate()
  }

  return (
    <form className="flex flex-col gap-6" onSubmit={submit}>
      <DialogHeader>
        <p className="eyebrow">Spaces</p>
        <DialogTitle>{space ? `Change ${space.name}` : 'Make a space'}</DialogTitle>
        <DialogDescription>
          {space
            ? 'Anyone in this space can rename it or change who’s in it.'
            : 'You’re in it, with the people you pick. They’ll find it in their spaces.'}
        </DialogDescription>
      </DialogHeader>
      <div className="grid gap-2">
        <Label htmlFor="space-name">Name</Label>
        <Input id="space-name" required maxLength={80} value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      <fieldset className="grid gap-1">
        <legend className="mb-2 text-sm font-medium">Who’s in it</legend>
        {choices.map((person, i) => (
          <label
            key={person.id}
            className="flex min-h-12 cursor-pointer items-center gap-3 rounded-xl px-2 transition-colors hover:bg-secondary has-focus-visible:ring-[3px] has-focus-visible:ring-ring/50"
          >
            <input
              type="checkbox"
              className="sr-only"
              checked={picked.has(person.id)}
              onChange={() => toggle(person.id)}
            />
            <Initial name={person.name} portrait={person.portrait} index={i + 1} />
            <span className="flex-1 truncate">{person.id === me ? `${person.name} (you)` : person.name}</span>
            <span
              aria-hidden="true"
              className={cn(
                'grid size-6 shrink-0 place-items-center rounded-md border-2',
                picked.has(person.id) ? 'border-brand bg-brand text-brand-foreground' : 'border-input',
              )}
            >
              {picked.has(person.id) && <Check className="size-3.5" strokeWidth={3} />}
            </span>
          </label>
        ))}
        {choices.length === 0 && (
          <p className="text-sub text-muted-foreground">
            No one else is in this circle yet. You can add people once they’ve joined.
          </p>
        )}
      </fieldset>
      <FormError error={save.error ?? leave.error} />
      <div className="flex flex-wrap items-center gap-3">
        <Button type="submit" size="lg" disabled={save.isPending || members.length === 0}>
          {space ? 'Save' : 'Make space'}
        </Button>
        {space && (
          <ConfirmButton
            label="Leave this space"
            title={`Leave ${space.name}?`}
            description="What you’ve shared there stays there. Anyone still in it can add you back."
            confirm="Leave"
            cancel="Stay"
            disabled={leave.isPending}
            onConfirm={() => leave.mutate()}
          />
        )}
      </div>
    </form>
  )
}
